// Year in Review ("Wrapped") — mobile-first swipeable slide sequence.
// Free-tier: NO PaywallGate — designed to drive top-of-funnel sharing.
// Slides are computed from /api/wrapped/{year} and only rendered when the
// underlying data exists for that slide (e.g. no top studio → skip it).

import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Loader2, ChevronLeft, ChevronRight, Sparkles, Film, Clock, Trophy,
  Star, Layers, ArrowRight, Building2, ImageDown, Home,
} from "lucide-react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { getWrapped } from "../lib/api";
import WrappedShareCard from "../components/WrappedShareCard";

const RED = "#FF3B30";

export default function Wrapped() {
  const { year } = useParams();
  const navigate = useNavigate();
  const yearNum = parseInt(year, 10);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!yearNum || yearNum < 2000 || yearNum > 2100) {
      setError("bad-year");
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getWrapped(yearNum)
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setError("generic"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [yearNum]);

  const slides = useMemo(() => buildSlides(data), [data]);

  if (loading) return <FullscreenLoader />;
  if (error) return <ErrorState kind={error} year={yearNum} onBack={() => navigate("/")} />;
  if (!data?.current?.has_data) return <EmptyState year={yearNum} onBack={() => navigate("/")} />;

  return <SlideDeck slides={slides} year={yearNum} data={data.current} />;
}

// ---------------------------------------------------------------------------
// Slide catalogue — each slide is `{ key, render }` and is filtered out
// upstream if its data isn't present.
// ---------------------------------------------------------------------------
function buildSlides(data) {
  if (!data?.current) return [];
  const cur = data.current;
  const prev = data.previous;
  const out = [];

  out.push({ key: "intro", render: () => <IntroSlide year={cur.year} /> });
  out.push({
    key: "totals",
    render: () => <TotalsSlide completed={cur.total_completed} hours={cur.total_hours} added={cur.total_added} />,
  });
  if (cur.top_genre) {
    out.push({ key: "genre", render: () => <TopGenreSlide genre={cur.top_genre} count={cur.top_genre_count} /> });
  }
  if (cur.top_studio) {
    out.push({ key: "studio", render: () => <TopStudioSlide studio={cur.top_studio} count={cur.top_studio_count} /> });
  }
  if (cur.longest_series) {
    out.push({ key: "longest", render: () => <SeriesHighlightSlide title="Longest Series" icon={<Layers className="h-5 w-5" />} entry={cur.longest_series} sub={`${cur.longest_series.episodes} episodes`} /> });
  }
  if (cur.highest_rated && cur.highest_rated.user_rating != null) {
    out.push({ key: "highest", render: () => <SeriesHighlightSlide title="Highest Rated" icon={<Trophy className="h-5 w-5" />} entry={cur.highest_rated} sub={`You gave it ${cur.highest_rated.user_rating}/10`} /> });
  }
  if (cur.persona?.personality_type) {
    out.push({ key: "persona", render: () => <PersonaSlide persona={cur.persona} /> });
  }
  if (prev?.has_data) {
    out.push({ key: "compare", render: () => <ComparisonSlide cur={cur} prev={prev} /> });
  }
  out.push({ key: "final", render: () => null }); // rendered inline in SlideDeck (needs share card refs)
  return out;
}

// ---------------------------------------------------------------------------
// Deck — swipe/tap/keyboard/arrows
// ---------------------------------------------------------------------------
function SlideDeck({ slides, year, data }) {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef(null);
  const touchDeltaX = useRef(0);

  const total = slides.length;
  const goTo = (i) => setIndex(Math.max(0, Math.min(total - 1, i)));
  const next = () => goTo(index + 1);
  const prev = () => goTo(index - 1);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, total]);  // eslint-disable-line react-hooks/exhaustive-deps

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchDeltaX.current = 0;
  };
  const onTouchMove = (e) => {
    if (touchStartX.current == null) return;
    touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
  };
  const onTouchEnd = () => {
    const dx = touchDeltaX.current;
    if (Math.abs(dx) > 50) {
      if (dx < 0) next();
      else prev();
    }
    touchStartX.current = null;
    touchDeltaX.current = 0;
  };

  const slide = slides[index];
  const isFinal = slide?.key === "final";

  return (
    <div
      data-testid="wrapped-deck"
      className="relative flex min-h-[calc(100vh-56px)] flex-col overflow-hidden bg-[#050505] text-white"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <ProgressBar total={total} index={index} onDotClick={goTo} />

      {/* Slide viewport */}
      <div
        data-testid={`wrapped-slide-${slide?.key}`}
        className="relative flex flex-1 items-center justify-center px-6 py-10 md:px-16"
        key={slide?.key}
        style={{ animation: "wrappedFadeIn 400ms ease-out" }}
      >
        {isFinal ? <FinalSlide year={year} data={data} onBack={() => setIndex(0)} /> : slide?.render?.()}
      </div>

      {/* Nav arrows (desktop) + tap zones (mobile) */}
      {index > 0 && (
        <button
          data-testid="wrapped-prev"
          onClick={prev}
          className="absolute left-4 top-1/2 hidden -translate-y-1/2 border border-white/15 bg-black/50 p-3 backdrop-blur transition-colors hover:border-[#FF3B30] hover:bg-[#FF3B30] md:block"
          aria-label="Previous slide"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
      )}
      {index < total - 1 && (
        <button
          data-testid="wrapped-next"
          onClick={next}
          className="absolute right-4 top-1/2 hidden -translate-y-1/2 border border-white/15 bg-black/50 p-3 backdrop-blur transition-colors hover:border-[#FF3B30] hover:bg-[#FF3B30] md:block"
          aria-label="Next slide"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      )}

      {/* Full-height mobile tap zones (except on the final slide, so the share
          button doesn't fight the tap) */}
      {!isFinal && (
        <>
          <button
            aria-label="Previous"
            className="absolute inset-y-0 left-0 w-1/3 md:hidden"
            onClick={prev}
            data-testid="wrapped-tap-left"
          />
          <button
            aria-label="Next"
            className="absolute inset-y-0 right-0 w-1/3 md:hidden"
            onClick={next}
            data-testid="wrapped-tap-right"
          />
        </>
      )}

      <style>{`
        @keyframes wrappedFadeIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
      `}</style>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Progress bar (Stories-style segmented dots at the top)
// ---------------------------------------------------------------------------
function ProgressBar({ total, index, onDotClick }) {
  return (
    <div
      data-testid="wrapped-progress"
      className="flex items-center gap-1.5 px-4 pt-4 md:px-8"
    >
      {Array.from({ length: total }).map((_, i) => (
        <button
          key={i}
          onClick={() => onDotClick(i)}
          data-testid={`wrapped-dot-${i}`}
          className="h-1 flex-1 overflow-hidden bg-white/15"
          aria-label={`Slide ${i + 1}`}
        >
          <span
            className="block h-full transition-all duration-300"
            style={{
              width: i < index ? "100%" : i === index ? "100%" : "0%",
              backgroundColor: i <= index ? RED : "transparent",
            }}
          />
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Slide components
// ---------------------------------------------------------------------------
function SlideChrome({ children, kicker }) {
  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col items-start text-left">
      {kicker && (
        <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-[#FF3B30]">// {kicker}</p>
      )}
      {children}
    </div>
  );
}

function IntroSlide({ year }) {
  return (
    <SlideChrome kicker="Year in Review">
      <h1 className="mt-4 font-display text-6xl uppercase leading-[0.9] tracking-tight text-white md:text-9xl">
        Your<br />{year}<br />Wrapped
      </h1>
      <p className="mt-6 max-w-md text-base text-neutral-400">
        A quick lap through your anime year — the hours, the genres, the shows that meant something. Tap through, then share the finale.
      </p>
      <p className="mt-8 font-mono text-[10px] uppercase tracking-widest text-neutral-600">
        Tap or swipe →
      </p>
    </SlideChrome>
  );
}

function TotalsSlide({ completed, hours, added }) {
  return (
    <SlideChrome kicker="The Big Numbers">
      <h2 className="mt-4 font-display text-5xl uppercase leading-none tracking-tight text-white md:text-7xl">
        You watched<br />
        <span className="text-[#FF3B30]">{hours}</span> hours<br />
        of anime.
      </h2>
      <div className="mt-8 grid w-full grid-cols-2 gap-4">
        <BigStat icon={<Film className="h-4 w-4" />} label="Completed" value={completed} />
        <BigStat icon={<Clock className="h-4 w-4" />} label="Total Added" value={added} />
      </div>
    </SlideChrome>
  );
}

function TopGenreSlide({ genre, count }) {
  return (
    <SlideChrome kicker="Your Comfort Zone">
      <h2 className="mt-4 font-display text-6xl uppercase leading-none tracking-tight text-white md:text-8xl">
        {genre}
      </h2>
      <p className="mt-6 max-w-md text-lg text-neutral-400">
        Your top genre this year. You leaned into it <span className="text-white">{count}</span> {count === 1 ? "time" : "times"}.
      </p>
    </SlideChrome>
  );
}

function TopStudioSlide({ studio, count }) {
  return (
    <SlideChrome kicker="House Style">
      <div className="mt-4 flex items-center gap-3">
        <Building2 className="h-6 w-6 text-[#FF3B30]" />
        <p className="font-mono text-xs uppercase tracking-widest text-neutral-400">Top Studio</p>
      </div>
      <h2 className="mt-3 font-display text-5xl uppercase leading-none tracking-tight text-white md:text-7xl">
        {studio}
      </h2>
      <p className="mt-6 max-w-md text-lg text-neutral-400">
        Made the most of what you watched — <span className="text-white">{count}</span> {count === 1 ? "title" : "titles"} in your year.
      </p>
    </SlideChrome>
  );
}

function SeriesHighlightSlide({ title, icon, entry, sub }) {
  return (
    <SlideChrome kicker={title}>
      <div className="mt-4 flex items-center gap-3 text-[#FF3B30]">{icon}</div>
      <div className="mt-4 flex flex-col gap-6 md:flex-row md:items-start">
        {entry.image_url && (
          <img
            src={entry.image_url}
            alt={entry.title}
            crossOrigin="anonymous"
            className="w-40 border border-white/15 object-cover md:w-52"
          />
        )}
        <div>
          <h2 className="font-display text-4xl uppercase leading-tight tracking-tight text-white md:text-6xl">
            {entry.title}
          </h2>
          <p className="mt-4 font-mono text-sm uppercase tracking-widest text-neutral-400">
            {sub}
          </p>
        </div>
      </div>
    </SlideChrome>
  );
}

function PersonaSlide({ persona }) {
  return (
    <SlideChrome kicker="Your Persona This Year">
      <Sparkles className="mt-4 h-6 w-6 text-[#FF3B30]" />
      <h2 className="mt-4 font-display text-5xl uppercase leading-[0.95] tracking-tight text-white md:text-7xl">
        {persona.personality_type}
      </h2>
      <p className="mt-6 max-w-lg text-lg text-neutral-300">
        {persona.core_description}
      </p>
      {persona.top_genre && (
        <p className="mt-4 font-mono text-xs uppercase tracking-widest text-neutral-500">
          Rooted in {persona.top_genre}
        </p>
      )}
    </SlideChrome>
  );
}

function ComparisonSlide({ cur, prev }) {
  const hoursDelta = cur.total_hours - prev.total_hours;
  const completedDelta = cur.total_completed - prev.total_completed;
  const trend = hoursDelta > 0 ? "up" : hoursDelta < 0 ? "down" : "flat";
  const trendLabel = trend === "up" ? "MORE" : trend === "down" ? "LESS" : "SAME";
  const trendColor = trend === "up" ? "#22c55e" : trend === "down" ? "#ef4444" : "#a3a3a3";
  return (
    <SlideChrome kicker={`vs. ${prev.year}`}>
      <h2 className="mt-4 font-display text-5xl uppercase leading-none tracking-tight text-white md:text-7xl">
        <span style={{ color: trendColor }}>{Math.abs(hoursDelta).toFixed(1)}</span> hours <span style={{ color: trendColor }}>{trendLabel}</span>
      </h2>
      <p className="mt-4 max-w-md text-sm text-neutral-400">
        You watched {trend === "up" ? "more" : trend === "down" ? "less" : "about the same"} this year than in {prev.year}.
      </p>
      <div className="mt-8 grid w-full grid-cols-2 gap-4">
        <CompareCard label={`${prev.year}`} completed={prev.total_completed} hours={prev.total_hours} genre={prev.top_genre} />
        <CompareCard label={`${cur.year}`} completed={cur.total_completed} hours={cur.total_hours} genre={cur.top_genre} accent />
      </div>
      {completedDelta !== 0 && (
        <p className="mt-6 font-mono text-[11px] uppercase tracking-widest text-neutral-500">
          {completedDelta > 0 ? "+" : ""}{completedDelta} completed titles year over year
        </p>
      )}
    </SlideChrome>
  );
}

function CompareCard({ label, completed, hours, genre, accent }) {
  return (
    <div className={`border p-4 ${accent ? "border-[#FF3B30] bg-[#FF3B30]/10" : "border-white/15 bg-white/[0.02]"}`}>
      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-400">{label}</p>
      <p className="mt-2 font-display text-3xl uppercase leading-none tracking-tight text-white">{hours} hrs</p>
      <p className="mt-1 font-mono text-[11px] uppercase tracking-widest text-neutral-500">
        {completed} completed · {genre || "—"}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Final slide — shareable card + download button
// ---------------------------------------------------------------------------
function FinalSlide({ year, data, onBack }) {
  const cardRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const download = async () => {
    if (!cardRef.current) return;
    setExporting(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        cacheBust: true,
        pixelRatio: 1,
        backgroundColor: "#050505",
      });
      const link = document.createElement("a");
      link.download = `otaku-index-${year}-wrapped.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Wrapped image downloaded");
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <SlideChrome kicker="That's a Wrap">
      <h2 className="mt-4 font-display text-5xl uppercase leading-none tracking-tight text-white md:text-7xl">
        {year} · Wrapped
      </h2>
      <p className="mt-4 max-w-lg text-sm text-neutral-400">
        Share your Otaku Index year with friends. Download the card below or restart the recap.
      </p>

      {/* Live preview of the shareable card (scaled) */}
      <div className="mt-8 w-full max-w-md overflow-hidden border border-white/15">
        <div
          data-testid="wrapped-card-preview"
          style={{
            width: "1080px",
            height: "1920px",
            transform: "scale(0.35)",
            transformOrigin: "top left",
            marginBottom: `${-1920 * 0.65}px`,
            marginRight: `${-1080 * 0.65}px`,
          }}
        >
          <WrappedShareCard data={data} />
        </div>
      </div>

      <div className="mt-8 flex flex-wrap gap-3">
        <button
          data-testid="wrapped-download-btn"
          onClick={download}
          disabled={exporting}
          className="flex items-center gap-2 border border-white/20 bg-[#FF3B30] px-5 py-3 font-mono text-[11px] uppercase tracking-widest text-white hover:bg-white hover:text-black disabled:opacity-50"
        >
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageDown className="h-4 w-4" />}
          Download 1080×1920
        </button>
        <button
          data-testid="wrapped-restart-btn"
          onClick={onBack}
          className="flex items-center gap-2 border border-white/20 bg-transparent px-5 py-3 font-mono text-[11px] uppercase tracking-widest text-white hover:border-[#FF3B30]"
        >
          <ArrowRight className="h-4 w-4 rotate-180" /> Replay
        </button>
        <Link
          to="/"
          data-testid="wrapped-home-link"
          className="flex items-center gap-2 border border-white/20 bg-transparent px-5 py-3 font-mono text-[11px] uppercase tracking-widest text-white hover:border-[#FF3B30]"
        >
          <Home className="h-4 w-4" /> Back to Catalogue
        </Link>
      </div>

      {/* Off-screen exact-size render for the PNG export. Kept in DOM but
          shifted off-canvas so html-to-image can measure at exact px. */}
      <div
        aria-hidden="true"
        style={{ position: "fixed", left: "-99999px", top: 0, pointerEvents: "none", opacity: 0 }}
      >
        <WrappedShareCard ref={cardRef} data={data} />
      </div>
    </SlideChrome>
  );
}

// ---------------------------------------------------------------------------
// Small building blocks
// ---------------------------------------------------------------------------
function BigStat({ icon, label, value }) {
  return (
    <div className="border border-white/15 bg-white/[0.02] p-4">
      <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
        {icon} {label}
      </p>
      <p className="mt-2 font-display text-5xl uppercase leading-none tracking-tight text-white">{value ?? 0}</p>
    </div>
  );
}

function FullscreenLoader() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-[#FF3B30]" />
    </div>
  );
}

function EmptyState({ year, onBack }) {
  return (
    <div className="mx-auto max-w-[900px] px-6 py-24 text-center" data-testid="wrapped-empty">
      <Star className="mx-auto h-10 w-10 text-neutral-600" />
      <h1 className="mt-4 font-display text-5xl uppercase text-white md:text-7xl">
        No {year} yet
      </h1>
      <p className="mx-auto mt-3 max-w-md text-sm text-neutral-400">
        You haven&apos;t added any anime dated to {year} — add a few shows to your catalogue and your Wrapped will appear here.
      </p>
      <button
        onClick={onBack}
        className="mt-6 inline-flex items-center gap-2 border border-white/20 bg-[#FF3B30] px-5 py-3 font-mono text-[11px] uppercase tracking-widest text-white hover:bg-white hover:text-black"
      >
        <ArrowRight className="h-4 w-4 rotate-180" /> Back to Catalogue
      </button>
    </div>
  );
}

function ErrorState({ kind, year, onBack }) {
  return (
    <div className="mx-auto max-w-[900px] px-6 py-24 text-center" data-testid="wrapped-error">
      <h1 className="font-display text-5xl uppercase text-white md:text-7xl">
        {kind === "bad-year" ? "Invalid year" : "Something broke"}
      </h1>
      <p className="mx-auto mt-3 max-w-md text-sm text-neutral-400">
        {kind === "bad-year" ? `${year} isn't a valid Wrapped year.` : "Try again in a moment."}
      </p>
      <button
        onClick={onBack}
        className="mt-6 inline-flex items-center gap-2 border border-white/20 bg-[#FF3B30] px-5 py-3 font-mono text-[11px] uppercase tracking-widest text-white hover:bg-white hover:text-black"
      >
        Back to Catalogue
      </button>
    </div>
  );
}
